import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { makeStyles } from "@mui/styles";
import {
    ColumnDef,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    PaginationState,
    RowSelectionState,
    SortingState,
    useReactTable
} from "@tanstack/react-table";
import React, { PropsWithChildren, ReactElement, ReactNode, useEffect, useMemo, useState } from "react";
import { Padding, PaddingObject } from "../../../util/StyleUtils";
import SettingsTableFallback from "../../Settings/Old/Table/SettingsTableFallback";
import QATableHeaderCell from "./Header/QATableHeaderCell";
import QAPagination from "./Pagination/QAPagination";
import QATableRow from "./QATableRow";
import QATableSelectionHeader from "./Selection/QATableSelectionHeader";

export interface ColumnDefinition<T> {
    id: string;
    title?: string;
    width: string;
    getValue: (value: T) => string | number | boolean | undefined;
    renderCell?: (value: T) => ReactNode;
    padding?: Padding;
    noWrap?: boolean;
    sortable?: boolean;
}

const useStyles = makeStyles({
    tableContainer: {
        overflowY: "auto",
        border: "1px solid #CDCDCD",
        borderTopRightRadius: "4px",
        borderTopLeftRadius: "4px",
        width: "100%",
        borderBottom: 0,
        flexGrow: 1
    },
    tableHeader: {
        height: "48px",
        "&>th": {
            overflow: "hidden"
        }
    },
    footerCell: {
        minHeight: "64px",
        maxHeight: "64px",
        backgroundColor: "#F6F8FA",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "16px",
        position: "relative",
        border: "1px solid #CCC",
        borderBottomRightRadius: "4px",
        borderBottomLeftRadius: "4px",
    }
});

interface Props<T> {
    items: T[];
    getId: (item: T) => string;
    columns: ColumnDefinition<T>[];
    fallbackText: string;

    defaultCellPadding?: PaddingObject;

    defaultSortByColumn?: string;
    defaultSortByColumnDesc?: boolean;
    defaultPageSize?: 10 | 25 | 50 | "all";

    getBackgroundColor?: (id: string) => string;
    onRowSelectionChanged?: (selected: string[]) => void;

    selectionColumnIndex?: number;

    testId?: string;
}

function QATable<T>(props: PropsWithChildren<Props<T>>): ReactElement | null {
    const classes = useStyles();

    const {
        items
    } = props;

    if (!props.defaultSortByColumn) {
        // eslint-disable-next-line no-console
        console.warn(`QATable [${props.testId}] has no default sort by column. Consider adding one.`);
    } else if (props.defaultSortByColumn) {
        const column = props.columns.find(c => c.id === props.defaultSortByColumn);
        if (column && !column.sortable) {
            // eslint-disable-next-line no-console
            console.warn(`QATable [${props.testId}] has an invalid default sort by column. Please use a sortable column instead.`);
        }
    }

    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const [pagination, setPagination] = useState<PaginationState>(props.defaultPageSize === "all" ? {
        pageSize: 99999,
        pageIndex: 0
    } : {
        pageSize: props.defaultPageSize ?? 25,
        pageIndex: 0
    });
    const [sorting, setSorting] = useState<SortingState>(props.defaultSortByColumn ? [{
        id: props.defaultSortByColumn,
        desc: props.defaultSortByColumnDesc ?? false
    }] : []);

    // Table Columns
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const columns: ColumnDef<T>[] = useMemo(
        () => {
            const finalColumns: ColumnDef<T>[] = [...props.columns.map(col => ({
                id: col.id,
                accessorFn: col.getValue,
                sortUndefined: 1
            }) as ColumnDef<T>)];
            if (props.onRowSelectionChanged) {
                const selectionColumn: ColumnDef<T> = {
                    id: "selection",
                    accessorFn: () => undefined
                };
                finalColumns.splice(props.selectionColumnIndex ?? 0, 0, selectionColumn);
            }
            return finalColumns;
        },
        [props.columns, props.onRowSelectionChanged, props.selectionColumnIndex]
    );

    const table = useReactTable(
        {
            state: {
                pagination,
                sorting,
                rowSelection
            },
            getRowId: props.getId,
            columns: columns,
            data: items,
            autoResetPageIndex: false,
            autoResetAll: false,
            sortingFns: {},
            getCoreRowModel: getCoreRowModel(),
            getSortedRowModel: getSortedRowModel(),
            getPaginationRowModel: getPaginationRowModel(),
            getFilteredRowModel: getFilteredRowModel(),
            enableRowSelection: true,
            onPaginationChange: setPagination,
            onSortingChange: setSorting,
            onRowSelectionChange: setRowSelection
        }
    );

    // Update selected rows
    useEffect(() => {
        const callback = props.onRowSelectionChanged;
        if (callback) {
            callback(Object
                .entries(rowSelection)
                .filter(([, value]) => value)
                .map(([key]) => key));
        }
    }, [rowSelection, props.onRowSelectionChanged]);

    // If filters are active, the page has to be reset automatically
    useEffect(() => {
        const pageCount = table.getPageCount();
        if (pagination.pageIndex >= pageCount && pageCount > 0) {
            setPagination(cur => ({
                ...cur,
                pageIndex: pageCount - 1
            }));
        }
    }, [pagination.pageIndex, table, props.items, pagination.pageSize]);

    const rows = table.getRowModel().rows.map(row => (
        <QATableRow
            key={props.getId(row.original)}
            row={row}
            style={props.getBackgroundColor ? { backgroundColor: props.getBackgroundColor(props.getId(row.original)) } : undefined}
            columns={props.columns}
            defaultCellPadding={props.defaultCellPadding}
            testId={`${props.testId}-QATableRow`} />
    ));

    const tableElement = (
        <Table
            size="small"
            stickyHeader>

            <TableHead>

                {table.getHeaderGroups().map(headerGroup => (

                    <TableRow
                        key={headerGroup.id}
                        className={classes.tableHeader}>

                        {headerGroup.headers.map(header => {
                            if (header.id === "selection") {
                                return (
                                    <QATableSelectionHeader
                                        key="__selection"
                                        table={table} />
                                );
                            }

                            const original = props.columns
                                .find(col => col.id === header.id);
                            if (!original) {
                                return null;
                            }

                            return (
                                <QATableHeaderCell
                                    key={original.id}
                                    defaultPadding={props.defaultCellPadding}
                                    header={header}
                                    config={original} />
                            );
                        })}
                    </TableRow>
                ))}
            </TableHead>

            <TableBody
                data-cy={`${props.testId}-TableBody`}>
                <SettingsTableFallback
                    visible={table.getRowModel().rows.length === 0}
                    message={props.fallbackText} />

                {rows}

            </TableBody>
        </Table>
    );

    return (
        <>
            <TableContainer
                data-cy={`${props.testId}-TableContainer`}
                className={classes.tableContainer}>

                {tableElement}

            </TableContainer>

            <div className={classes.footerCell}>
                <QAPagination
                    readonly={props.defaultPageSize === "all"}
                    createResultString={(first, last, total) => (props.defaultPageSize === "all"
                        ? `${total} ${total === 1 ? "Element" : "Elemente"}`
                        : `${first} bis ${last} von ${total}`)}
                    currentPage={pagination.pageIndex}
                    currentPageSize={pagination.pageSize}
                    onChangePageSize={value => setPagination(cur => ({
                        ...cur,
                        pageSize: value
                    }))}
                    onNavigateToPage={value => setPagination(cur => ({
                        ...cur,
                        pageIndex: value
                    }))}
                    pageCount={table.getPageCount()}
                    pageSizeOptions={[10, 25, 50]}
                    rowCount={table.getCoreRowModel().rows.length} />
            </div>
        </>
    );
}

export default QATable;
